const SHA256_DIGEST_PATTERN = /^[a-f0-9]{64}$/;
const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;
const HEX_COLOR_PATTERN = /^#[a-fA-F0-9]{6}(?:[a-fA-F0-9]{2})?$/;
const FIGMA_SOURCE_KEYS = [
  "kind",
  "fileKey",
  "nodeId",
  "url",
  "observedAt",
  "upstreamVersion",
  "upstreamHash",
] as const;
const OPENPENCIL_SOURCE_KEYS = [
  "kind",
  "sourceId",
  "sourceRevision",
  "sha256",
  "artifactRef",
  "upstreamFigma",
] as const;
const OPERATION_KEYS = [
  "operationId",
  "kind",
  "sourceTarget",
  "cogoTarget",
  "nextValue",
] as const;
const SOURCE_TARGET_KEYS = [
  "nodeId",
  "expectedNodeType",
  "expectedName",
] as const;
const COGO_TARGET_KEYS = [
  "blockName",
  "elementId",
  "sourceNodeId",
  "jsonPointer",
  "expectedValue",
] as const;
const GEOMETRY_KEYS = ["x", "y", "width", "height", "rotation"] as const;
const SOLID_FILL_KEYS = ["fillIndex", "expectedColor", "color"] as const;

export type DesignMutationKind =
  | "rename-node"
  | "set-text"
  | "set-visible"
  | "set-opacity"
  | "set-geometry"
  | "set-solid-fill";

export interface FigmaDesignSourceReference {
  kind: "figma-reference";
  fileKey: string;
  nodeId: string;
  url: string;
  observedAt: string;
  upstreamVersion?: string;
  upstreamHash?: string;
}

export interface OpenPencilDesignSourceReference {
  kind: "openpencil-revision";
  sourceId: string;
  sourceRevision: number;
  sha256: string;
  artifactRef: string;
  upstreamFigma?: FigmaDesignSourceReference;
}

export type ExternalDesignSourceReference =
  | FigmaDesignSourceReference
  | OpenPencilDesignSourceReference;

export interface DesignMutationGeometry {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
}

export interface DesignMutationSolidFill {
  fillIndex: number;
  expectedColor?: string;
  color: string;
}

export interface DesignMutationSourceTarget {
  nodeId: string;
  expectedNodeType: string;
  expectedName?: string;
}

export interface DesignMutationCogoTarget {
  blockName: string;
  elementId: string;
  sourceNodeId: string;
  jsonPointer: string;
  expectedValue?: unknown;
}

interface DesignMutationOperationBase {
  operationId: string;
  kind: DesignMutationKind;
  sourceTarget: DesignMutationSourceTarget;
  cogoTarget: DesignMutationCogoTarget;
}

export interface RenameNodeDesignMutationOperation extends DesignMutationOperationBase {
  kind: "rename-node";
  nextValue: string;
}

export interface SetTextDesignMutationOperation extends DesignMutationOperationBase {
  kind: "set-text";
  nextValue: string;
}

export interface SetVisibleDesignMutationOperation extends DesignMutationOperationBase {
  kind: "set-visible";
  nextValue: boolean;
}

export interface SetOpacityDesignMutationOperation extends DesignMutationOperationBase {
  kind: "set-opacity";
  nextValue: number;
}

export interface SetGeometryDesignMutationOperation extends DesignMutationOperationBase {
  kind: "set-geometry";
  nextValue: DesignMutationGeometry;
}

export interface SetSolidFillDesignMutationOperation extends DesignMutationOperationBase {
  kind: "set-solid-fill";
  nextValue: DesignMutationSolidFill;
}

export type DesignMutationOperation =
  | RenameNodeDesignMutationOperation
  | SetTextDesignMutationOperation
  | SetVisibleDesignMutationOperation
  | SetOpacityDesignMutationOperation
  | SetGeometryDesignMutationOperation
  | SetSolidFillDesignMutationOperation;

export interface DesignMutationActor {
  kind: "human" | "agent";
  id: string;
}

export interface HumanDesignMutationActor extends DesignMutationActor {
  kind: "human";
}

export interface CogoProjectRevisionReference {
  projectId: string;
  revision: number;
  sha256: string;
}

export interface DesignMutationProposal {
  schemaVersion: "cogo.design-mutation-proposal.v1";
  mutationId: string;
  source: ExternalDesignSourceReference;
  baseCogoProject: CogoProjectRevisionReference;
  actor: DesignMutationActor;
  reason: string;
  operations: DesignMutationOperation[];
  requestedAt: string;
  evidenceRefs: string[];
  status: "awaiting-confirmation";
  finalJsonWriter: false;
  contentHash: string;
}

export interface DesignMutation {
  schemaVersion: "cogo.design-mutation.v1";
  mutationId: string;
  proposalRef: {
    mutationId: string;
    proposalHash: string;
  };
  source: ExternalDesignSourceReference;
  baseCogoProject: CogoProjectRevisionReference;
  actor: DesignMutationActor;
  reason: string;
  operations: DesignMutationOperation[];
  requestedAt: string;
  evidenceRefs: string[];
  approvedBy: HumanDesignMutationActor;
  approvedAt: string;
  status: "approved";
  finalJsonWriter: false;
  contentHash: string;
}

export interface CreateDesignMutationProposalInput {
  mutationId: string;
  source: ExternalDesignSourceReference;
  baseCogoProject: CogoProjectRevisionReference;
  actor: DesignMutationActor;
  reason: string;
  operations: DesignMutationOperation[];
  requestedAt: string;
  evidenceRefs: string[];
}

export interface ApproveDesignMutationInput {
  actor: DesignMutationActor;
  approvedAt: string;
}

export function createDesignMutationProposal(
  input: CreateDesignMutationProposalInput,
): DesignMutationProposal {
  assertExactKeys(
    input,
    [
      "mutationId",
      "source",
      "baseCogoProject",
      "actor",
      "reason",
      "operations",
      "requestedAt",
      "evidenceRefs",
    ],
    "DESIGN_MUTATION_PROPOSAL_INPUT",
  );
  assertProposalFields(input);

  const base = {
    schemaVersion: "cogo.design-mutation-proposal.v1" as const,
    mutationId: input.mutationId,
    source: normalizeSource(input.source),
    baseCogoProject: { ...input.baseCogoProject },
    actor: { ...input.actor },
    reason: input.reason,
    operations: input.operations.map(normalizeOperation),
    requestedAt: input.requestedAt,
    evidenceRefs: uniqueSorted(input.evidenceRefs),
    status: "awaiting-confirmation" as const,
    finalJsonWriter: false as const,
  };
  return { ...base, contentHash: hash(base) };
}

/**
 * Produces the canonical content-addressed hash used by DesignMutation.
 * The input must be JSON-safe so the same value hashes identically in Node and
 * browser runtimes.
 */
export function computeDesignContentHash(value: unknown): string {
  assertJsonValue(value, "DESIGN_CONTENT_HASH_INPUT");
  return `sha256:${sha256Hex(stableStringify(value))}`;
}

export function approveDesignMutation(
  proposal: DesignMutationProposal,
  input: ApproveDesignMutationInput,
): DesignMutation {
  assertDesignMutationProposal(proposal);
  assertExactKeys(input, ["actor", "approvedAt"], "DESIGN_MUTATION_APPROVAL");
  assertActor(input.actor, "DESIGN_MUTATION_APPROVER");
  if (input.actor.kind !== "human") {
    throw new Error("DESIGN_MUTATION_HUMAN_APPROVAL_REQUIRED");
  }
  assertIsoTimestamp(input.approvedAt, "DESIGN_MUTATION_APPROVED_AT");

  const base = {
    schemaVersion: "cogo.design-mutation.v1" as const,
    mutationId: proposal.mutationId,
    proposalRef: {
      mutationId: proposal.mutationId,
      proposalHash: proposal.contentHash,
    },
    source: normalizeSource(proposal.source),
    baseCogoProject: { ...proposal.baseCogoProject },
    actor: { ...proposal.actor },
    reason: proposal.reason,
    operations: proposal.operations.map(normalizeOperation),
    requestedAt: proposal.requestedAt,
    evidenceRefs: [...proposal.evidenceRefs],
    approvedBy: { ...input.actor } as HumanDesignMutationActor,
    approvedAt: input.approvedAt,
    status: "approved" as const,
    finalJsonWriter: false as const,
  };
  const mutation = { ...base, contentHash: hash(base) };
  assertDesignMutationExecutable(mutation);
  return mutation;
}

export function assertDesignMutationExecutable(
  value: unknown,
): asserts value is DesignMutation {
  assertRecord(value, "DESIGN_MUTATION");
  assertExactKeys(
    value,
    [
      "schemaVersion",
      "mutationId",
      "proposalRef",
      "source",
      "baseCogoProject",
      "actor",
      "reason",
      "operations",
      "requestedAt",
      "evidenceRefs",
      "approvedBy",
      "approvedAt",
      "status",
      "finalJsonWriter",
      "contentHash",
    ],
    "DESIGN_MUTATION",
  );
  if (value.schemaVersion !== "cogo.design-mutation.v1") {
    throw new Error("INVALID_DESIGN_MUTATION_SCHEMA_VERSION");
  }
  if (value.status !== "approved") {
    throw new Error("DESIGN_MUTATION_NOT_APPROVED");
  }
  if (value.finalJsonWriter !== false) {
    throw new Error("DESIGN_MUTATION_FINAL_JSON_WRITER_FORBIDDEN");
  }

  assertProposalFields(value as unknown as CreateDesignMutationProposalInput);
  assertRecord(value.proposalRef, "DESIGN_MUTATION_PROPOSAL_REF");
  assertExactKeys(
    value.proposalRef,
    ["mutationId", "proposalHash"],
    "DESIGN_MUTATION_PROPOSAL_REF",
  );
  assertNonEmptyString(
    value.proposalRef.mutationId,
    "DESIGN_MUTATION_PROPOSAL_REF_ID",
  );
  if (value.proposalRef.mutationId !== value.mutationId) {
    throw new Error("DESIGN_MUTATION_PROPOSAL_ID_MISMATCH");
  }
  assertContentHash(
    value.proposalRef.proposalHash,
    "DESIGN_MUTATION_PROPOSAL_REF",
  );
  assertActor(value.approvedBy, "DESIGN_MUTATION_APPROVER");
  if (value.approvedBy.kind !== "human") {
    throw new Error("DESIGN_MUTATION_HUMAN_APPROVAL_REQUIRED");
  }
  assertIsoTimestamp(value.approvedAt, "DESIGN_MUTATION_APPROVED_AT");
  assertContentHash(value.contentHash, "DESIGN_MUTATION");

  const { contentHash: _contentHash, ...mutationBase } = value;
  if (hash(mutationBase) !== value.contentHash) {
    throw new Error("INVALID_DESIGN_MUTATION_CONTENT_HASH");
  }

  const proposalBase = proposalBaseFromApprovedMutation(
    value as unknown as DesignMutation,
  );
  if (hash(proposalBase) !== value.proposalRef.proposalHash) {
    throw new Error("INVALID_DESIGN_MUTATION_PROPOSAL_REF_HASH");
  }
}

export function assertDesignMutationProposal(
  value: unknown,
): asserts value is DesignMutationProposal {
  assertRecord(value, "DESIGN_MUTATION_PROPOSAL");
  assertExactKeys(
    value,
    [
      "schemaVersion",
      "mutationId",
      "source",
      "baseCogoProject",
      "actor",
      "reason",
      "operations",
      "requestedAt",
      "evidenceRefs",
      "status",
      "finalJsonWriter",
      "contentHash",
    ],
    "DESIGN_MUTATION_PROPOSAL",
  );
  if (value.schemaVersion !== "cogo.design-mutation-proposal.v1") {
    throw new Error("INVALID_DESIGN_MUTATION_PROPOSAL_SCHEMA_VERSION");
  }
  if (value.status !== "awaiting-confirmation") {
    throw new Error("DESIGN_MUTATION_PROPOSAL_NOT_AWAITING_CONFIRMATION");
  }
  if (value.finalJsonWriter !== false) {
    throw new Error("DESIGN_MUTATION_FINAL_JSON_WRITER_FORBIDDEN");
  }
  assertProposalFields(value as unknown as CreateDesignMutationProposalInput);
  assertContentHash(value.contentHash, "DESIGN_MUTATION_PROPOSAL");
  const { contentHash: _contentHash, ...base } = value;
  if (hash(base) !== value.contentHash) {
    throw new Error("INVALID_DESIGN_MUTATION_PROPOSAL_CONTENT_HASH");
  }
}

function assertProposalFields(input: CreateDesignMutationProposalInput): void {
  assertNonEmptyString(input.mutationId, "DESIGN_MUTATION_ID");
  assertExternalDesignSourceReference(input.source);
  assertCogoProjectRevision(input.baseCogoProject);
  assertActor(input.actor, "DESIGN_MUTATION_ACTOR");
  assertNonEmptyString(input.reason, "DESIGN_MUTATION_REASON");
  assertIsoTimestamp(input.requestedAt, "DESIGN_MUTATION_REQUESTED_AT");
  if (!Array.isArray(input.operations)) {
    throw new Error("INVALID_DESIGN_MUTATION_OPERATIONS");
  }
  if (input.operations.length < 1 || input.operations.length > 100) {
    throw new Error("DESIGN_MUTATION_OPERATIONS_OUT_OF_BOUNDS");
  }
  if (!Array.isArray(input.evidenceRefs)) {
    throw new Error("INVALID_DESIGN_MUTATION_EVIDENCE_REFS");
  }
  for (const evidenceRef of input.evidenceRefs) {
    assertNonEmptyString(evidenceRef, "DESIGN_MUTATION_EVIDENCE_REF");
  }

  const operationIds = new Set<string>();
  const sourceNodeKinds = new Set<string>();
  const cogoPointers = new Set<string>();
  for (const operation of input.operations) {
    assertDesignMutationOperation(operation);
    if (operationIds.has(operation.operationId)) {
      throw new Error("DUPLICATE_DESIGN_MUTATION_OPERATION_ID");
    }
    operationIds.add(operation.operationId);
    const sourceNodeKind = `${operation.sourceTarget.nodeId}\u0000${operation.kind}`;
    if (sourceNodeKinds.has(sourceNodeKind)) {
      throw new Error("DUPLICATE_DESIGN_MUTATION_SOURCE_NODE_KIND");
    }
    sourceNodeKinds.add(sourceNodeKind);
    if (cogoPointers.has(operation.cogoTarget.jsonPointer)) {
      throw new Error("DUPLICATE_DESIGN_MUTATION_COGO_JSON_POINTER");
    }
    cogoPointers.add(operation.cogoTarget.jsonPointer);
  }
}

function assertExternalDesignSourceReference(
  source: unknown,
): asserts source is ExternalDesignSourceReference {
  assertRecord(source, "EXTERNAL_DESIGN_SOURCE_REFERENCE");
  if (source.kind === "figma-reference") {
    assertFigmaSourceReference(source);
    return;
  }
  if (source.kind === "openpencil-revision") {
    assertExactKeys(
      source,
      OPENPENCIL_SOURCE_KEYS,
      "OPENPENCIL_DESIGN_SOURCE_REFERENCE",
    );
    assertNonEmptyString(source.sourceId, "OPENPENCIL_SOURCE_ID");
    assertNonNegativeInteger(
      source.sourceRevision,
      "OPENPENCIL_SOURCE_REVISION",
    );
    assertSha256Digest(source.sha256, "OPENPENCIL_SOURCE");
    assertNonEmptyString(source.artifactRef, "OPENPENCIL_ARTIFACT_REF");
    if (source.upstreamFigma !== undefined) {
      assertFigmaSourceReference(source.upstreamFigma);
    }
    return;
  }
  throw new Error("INVALID_EXTERNAL_DESIGN_SOURCE_KIND");
}

function assertFigmaSourceReference(
  source: unknown,
): asserts source is FigmaDesignSourceReference {
  assertRecord(source, "FIGMA_DESIGN_SOURCE_REFERENCE");
  assertExactKeys(source, FIGMA_SOURCE_KEYS, "FIGMA_DESIGN_SOURCE_REFERENCE");
  if (source.kind !== "figma-reference") {
    throw new Error("INVALID_FIGMA_DESIGN_SOURCE_KIND");
  }
  assertNonEmptyString(source.fileKey, "FIGMA_FILE_KEY");
  assertNonEmptyString(source.nodeId, "FIGMA_NODE_ID");
  assertHttpUrl(source.url, "FIGMA_URL");
  assertIsoTimestamp(source.observedAt, "FIGMA_OBSERVED_AT");
  if (source.upstreamVersion !== undefined) {
    assertNonEmptyString(source.upstreamVersion, "FIGMA_UPSTREAM_VERSION");
  }
  if (source.upstreamHash !== undefined) {
    assertSha256Digest(source.upstreamHash, "FIGMA_UPSTREAM");
  }
}

function assertCogoProjectRevision(source: unknown): void {
  assertRecord(source, "COGO_PROJECT_REVISION_REFERENCE");
  assertExactKeys(
    source,
    ["projectId", "revision", "sha256"],
    "COGO_PROJECT_REVISION_REFERENCE",
  );
  assertNonEmptyString(source.projectId, "COGO_PROJECT_ID");
  assertNonNegativeInteger(source.revision, "COGO_PROJECT_REVISION");
  assertSha256Digest(source.sha256, "COGO_PROJECT_REVISION");
}

function assertActor(
  actor: unknown,
  label: string,
): asserts actor is DesignMutationActor {
  assertRecord(actor, label);
  assertExactKeys(actor, ["kind", "id"], label);
  if (actor.kind !== "human" && actor.kind !== "agent") {
    throw new Error(`INVALID_${label}_KIND`);
  }
  assertNonEmptyString(actor.id, `${label}_ID`);
}

function assertDesignMutationOperation(
  operation: unknown,
): asserts operation is DesignMutationOperation {
  assertRecord(operation, "DESIGN_MUTATION_OPERATION");
  assertExactKeys(operation, OPERATION_KEYS, "DESIGN_MUTATION_OPERATION");
  assertNonEmptyString(operation.operationId, "DESIGN_MUTATION_OPERATION_ID");
  if (!isDesignMutationKind(operation.kind)) {
    throw new Error("INVALID_DESIGN_MUTATION_KIND");
  }
  assertSourceTarget(operation.sourceTarget);
  assertCogoTarget(operation.cogoTarget);
  if (operation.sourceTarget.nodeId !== operation.cogoTarget.sourceNodeId) {
    throw new Error("DESIGN_MUTATION_SOURCE_NODE_ID_MISMATCH");
  }

  switch (operation.kind) {
    case "rename-node":
      assertNonEmptyString(
        operation.nextValue,
        "DESIGN_MUTATION_NEXT_NODE_NAME",
      );
      break;
    case "set-text":
      if (typeof operation.nextValue !== "string") {
        throw new Error("INVALID_DESIGN_MUTATION_NEXT_TEXT");
      }
      break;
    case "set-visible":
      if (typeof operation.nextValue !== "boolean") {
        throw new Error("INVALID_DESIGN_MUTATION_VISIBILITY");
      }
      break;
    case "set-opacity":
      if (
        typeof operation.nextValue !== "number" ||
        !Number.isFinite(operation.nextValue) ||
        operation.nextValue < 0 ||
        operation.nextValue > 1
      ) {
        throw new Error("INVALID_DESIGN_MUTATION_OPACITY");
      }
      break;
    case "set-geometry":
      assertGeometry(operation.nextValue);
      break;
    case "set-solid-fill":
      assertSolidFill(operation.nextValue);
      break;
  }
}

function assertSourceTarget(
  target: unknown,
): asserts target is DesignMutationSourceTarget {
  assertRecord(target, "DESIGN_MUTATION_SOURCE_TARGET");
  assertExactKeys(target, SOURCE_TARGET_KEYS, "DESIGN_MUTATION_SOURCE_TARGET");
  assertNonEmptyString(target.nodeId, "DESIGN_MUTATION_SOURCE_NODE_ID");
  assertNonEmptyString(
    target.expectedNodeType,
    "DESIGN_MUTATION_EXPECTED_NODE_TYPE",
  );
  if (target.expectedName !== undefined) {
    assertNonEmptyString(
      target.expectedName,
      "DESIGN_MUTATION_EXPECTED_NODE_NAME",
    );
  }
}

function assertCogoTarget(
  target: unknown,
): asserts target is DesignMutationCogoTarget {
  assertRecord(target, "DESIGN_MUTATION_COGO_TARGET");
  assertExactKeys(target, COGO_TARGET_KEYS, "DESIGN_MUTATION_COGO_TARGET");
  assertNonEmptyString(target.blockName, "DESIGN_MUTATION_COGO_BLOCK_NAME");
  assertNonEmptyString(target.elementId, "DESIGN_MUTATION_COGO_ELEMENT_ID");
  assertNonEmptyString(
    target.sourceNodeId,
    "DESIGN_MUTATION_COGO_SOURCE_NODE_ID",
  );
  assertNonEmptyString(target.jsonPointer, "DESIGN_MUTATION_COGO_JSON_POINTER");
  if (!target.jsonPointer.startsWith("/blocks/")) {
    throw new Error("INVALID_DESIGN_MUTATION_COGO_JSON_POINTER");
  }
  if (hasOwn(target, "expectedValue")) {
    assertJsonValue(target.expectedValue, "DESIGN_MUTATION_EXPECTED_VALUE");
  }
}

function assertGeometry(value: unknown): void {
  assertRecord(value, "DESIGN_MUTATION_GEOMETRY");
  assertExactKeys(value, GEOMETRY_KEYS, "DESIGN_MUTATION_GEOMETRY");
  if (!GEOMETRY_KEYS.some((key) => value[key] !== undefined)) {
    throw new Error("DESIGN_MUTATION_GEOMETRY_CHANGE_REQUIRED");
  }
  assertOptionalBoundedNumber(value.x, -1_000_000, 1_000_000, "GEOMETRY_X");
  assertOptionalBoundedNumber(value.y, -1_000_000, 1_000_000, "GEOMETRY_Y");
  assertOptionalBoundedNumber(
    value.width,
    0,
    1_000_000,
    "GEOMETRY_WIDTH",
    true,
  );
  assertOptionalBoundedNumber(
    value.height,
    0,
    1_000_000,
    "GEOMETRY_HEIGHT",
    true,
  );
  assertOptionalBoundedNumber(value.rotation, -360, 360, "GEOMETRY_ROTATION");
}

function assertSolidFill(value: unknown): void {
  assertRecord(value, "DESIGN_MUTATION_SOLID_FILL");
  assertExactKeys(value, SOLID_FILL_KEYS, "DESIGN_MUTATION_SOLID_FILL");
  if (
    typeof value.fillIndex !== "number" ||
    !Number.isInteger(value.fillIndex) ||
    value.fillIndex < 0
  ) {
    throw new Error("INVALID_DESIGN_MUTATION_SOLID_FILL_INDEX");
  }
  assertHexColor(value.color, "DESIGN_MUTATION_SOLID_FILL_COLOR");
  if (value.expectedColor !== undefined) {
    assertHexColor(
      value.expectedColor,
      "DESIGN_MUTATION_SOLID_FILL_EXPECTED_COLOR",
    );
  }
}

function assertHexColor(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !HEX_COLOR_PATTERN.test(value)) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertOptionalBoundedNumber(
  value: unknown,
  minimum: number,
  maximum: number,
  label: string,
  exclusiveMinimum = false,
): void {
  if (value === undefined) return;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    (exclusiveMinimum ? value <= minimum : value < minimum) ||
    value > maximum
  ) {
    throw new Error(`INVALID_DESIGN_MUTATION_${label}`);
  }
}

function normalizeSource(
  source: ExternalDesignSourceReference,
): ExternalDesignSourceReference {
  if (source.kind === "figma-reference") {
    return {
      kind: source.kind,
      fileKey: source.fileKey,
      nodeId: source.nodeId,
      url: source.url,
      observedAt: source.observedAt,
      ...(source.upstreamVersion !== undefined
        ? { upstreamVersion: source.upstreamVersion }
        : {}),
      ...(source.upstreamHash !== undefined
        ? { upstreamHash: source.upstreamHash }
        : {}),
    };
  }
  return {
    kind: source.kind,
    sourceId: source.sourceId,
    sourceRevision: source.sourceRevision,
    sha256: source.sha256,
    artifactRef: source.artifactRef,
    ...(source.upstreamFigma
      ? { upstreamFigma: normalizeSource(source.upstreamFigma) }
      : {}),
  } as OpenPencilDesignSourceReference;
}

function normalizeOperation(
  operation: DesignMutationOperation,
): DesignMutationOperation {
  const normalized = {
    operationId: operation.operationId,
    kind: operation.kind,
    sourceTarget: {
      nodeId: operation.sourceTarget.nodeId,
      expectedNodeType: operation.sourceTarget.expectedNodeType,
      ...(operation.sourceTarget.expectedName !== undefined
        ? { expectedName: operation.sourceTarget.expectedName }
        : {}),
    },
    cogoTarget: {
      blockName: operation.cogoTarget.blockName,
      elementId: operation.cogoTarget.elementId,
      sourceNodeId: operation.cogoTarget.sourceNodeId,
      jsonPointer: operation.cogoTarget.jsonPointer,
      ...(hasOwn(operation.cogoTarget, "expectedValue")
        ? { expectedValue: cloneJsonValue(operation.cogoTarget.expectedValue) }
        : {}),
    },
    nextValue:
      operation.kind === "set-geometry" ||
      operation.kind === "set-solid-fill"
        ? { ...operation.nextValue }
        : operation.nextValue,
  };
  return normalized as DesignMutationOperation;
}

function proposalBaseFromApprovedMutation(
  mutation: DesignMutation,
): Omit<DesignMutationProposal, "contentHash"> {
  return {
    schemaVersion: "cogo.design-mutation-proposal.v1",
    mutationId: mutation.mutationId,
    source: mutation.source,
    baseCogoProject: mutation.baseCogoProject,
    actor: mutation.actor,
    reason: mutation.reason,
    operations: mutation.operations,
    requestedAt: mutation.requestedAt,
    evidenceRefs: mutation.evidenceRefs,
    status: "awaiting-confirmation",
    finalJsonWriter: false,
  };
}

function isDesignMutationKind(value: unknown): value is DesignMutationKind {
  return (
    value === "rename-node" ||
    value === "set-text" ||
    value === "set-visible" ||
    value === "set-opacity" ||
    value === "set-geometry" ||
    value === "set-solid-fill"
  );
}

function assertNonEmptyString(
  value: unknown,
  label: string,
): asserts value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertNonNegativeInteger(value: unknown, label: string): void {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertSha256Digest(
  value: unknown,
  label: string,
): asserts value is string {
  if (typeof value !== "string" || !SHA256_DIGEST_PATTERN.test(value)) {
    throw new Error(`INVALID_${label}_SHA256`);
  }
}

function assertContentHash(
  value: unknown,
  label: string,
): asserts value is string {
  if (typeof value !== "string" || !CONTENT_HASH_PATTERN.test(value)) {
    throw new Error(`INVALID_${label}_CONTENT_HASH`);
  }
}

function assertIsoTimestamp(value: unknown, label: string): void {
  assertNonEmptyString(value, label);
  const timestamp = Date.parse(value);
  if (
    !Number.isFinite(timestamp) ||
    new Date(timestamp).toISOString() !== value
  ) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertHttpUrl(value: unknown, label: string): void {
  assertNonEmptyString(value, label);
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`INVALID_${label}`);
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error(`INVALID_${label}`);
  }
}

function assertJsonValue(value: unknown, label: string): void {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error(`INVALID_${label}`);
    return;
  }
  if (Array.isArray(value)) {
    for (const nested of value) assertJsonValue(nested, label);
    return;
  }
  if (isRecord(value)) {
    if (Object.getPrototypeOf(value) !== Object.prototype) {
      throw new Error(`INVALID_${label}`);
    }
    for (const nested of Object.values(value)) assertJsonValue(nested, label);
    return;
  }
  throw new Error(`INVALID_${label}`);
}

function cloneJsonValue(value: unknown): unknown {
  return JSON.parse(stableStringify(value)) as unknown;
}

function assertRecord(
  value: unknown,
  label: string,
): asserts value is Record<string, unknown> {
  if (!isRecord(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new Error(`INVALID_${label}`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function assertExactKeys(
  value: object,
  allowedKeys: readonly string[],
  label: string,
): void {
  const allowed = new Set(allowedKeys);
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) throw new Error(`UNKNOWN_${label}_FIELD: ${key}`);
  }
}

function hasOwn(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function hash(value: unknown): string {
  return computeDesignContentHash(value);
}

function stableStringify(value: unknown): string {
  return JSON.stringify(stableObject(value));
}

function stableObject(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableObject);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, nested]) => [key, stableObject(nested)]),
  );
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort();
}

// Synchronous, browser-safe SHA-256. Keeping this implementation local avoids
// importing node:crypto into the Studio/Vite runtime and preserves the package's
// dependency-free contract.
function sha256Hex(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const bitLength = bytes.length * 8;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;
  const schedule = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      schedule[index] = view.getUint32(offset + index * 4);
    }
    for (let index = 16; index < 64; index += 1) {
      const left = schedule[index - 15];
      const right = schedule[index - 2];
      const sigma0 =
        rotateRight(left, 7) ^ rotateRight(left, 18) ^ (left >>> 3);
      const sigma1 =
        rotateRight(right, 17) ^ rotateRight(right, 19) ^ (right >>> 10);
      schedule[index] =
        (schedule[index - 16] + sigma0 + schedule[index - 7] + sigma1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 =
        (h +
          sum1 +
          choice +
          SHA256_ROUND_CONSTANTS[index] +
          schedule[index]) >>>
        0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((word) => word.toString(16).padStart(8, "0"))
    .join("");
}

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

const SHA256_ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
